import AgoraRTM, { RtmChannel, RtmClient, RtmMessage, RtmStatusCode } from 'agora-rtm-sdk'
import EventEmitter from 'events'

import { DeMessage, EncodeOrDecode, MessageType } from './encodeOrDecode'

export interface RtmOptions {
  channel: string
  uid: number
  token?: string
  version?: number
}

export default class Rtm extends EventEmitter {
  private appId: string
  private client: RtmClient | null = null
  private channelClient: RtmChannel | undefined = undefined
  private channel: string = ''
  private token: string | undefined = undefined
  private msgVersion: number = 3
  private uid: number = 0

  constructor(appId: string) {
    super()
    this.appId = appId
  }

  public async init({ uid, channel, token, version }: RtmOptions) {
    this.uid = uid
    this.channel = channel
    this.token = token
    this.msgVersion = version || this.msgVersion
    this.client = AgoraRTM.createInstance(this.appId, {
      logFilter: AgoraRTM.LOG_FILTER_WARNING
    })
    this.subscribeClientEvents(this.client)
  }

  public async join() {
    if (!this.client) return
    await this.client.login({ token: this.token, uid: `${this.uid}` })
    this.channelClient = this.client.createChannel(this.channel) // 此处传入频道 ID
    this.subscribeChannelClientEvents(this.channelClient)
    await this.channelClient.join()
    super.emit('join-room', this.uid)
  }

  private subscribeClientEvents(client: RtmClient) {
    client.on('ConnectionStateChanged', (newState: RtmStatusCode.ConnectionState, reason: RtmStatusCode.ConnectionChangeReason) => {
      console.info('RTM ConnectionStateChanged', newState, reason)
      if (reason === 'REMOTE_LOGIN') {
        super.emit('user-kicked')
      } else if (reason === 'LOGIN_FAILURE') {
        super.emit('join-fail')
      } else if (reason === 'LOGIN_TIMEOUT') {
        super.emit('login-timeout')
      } else if (reason === 'INTERRUPTED') {
        super.emit('login-interrupted')
      }
    })
  }

  private subscribeChannelClientEvents(channelClient: RtmChannel) {
    channelClient.on('MemberLeft', (userId: string) => {
      super.emit('user-leave', userId)
    })
    channelClient.on('MemberJoined', (userId: string) => {
      super.emit('user-add', userId)
    })
    channelClient.on('ChannelMessage', ({ text }: RtmMessage, memberId: string) => {
      if (!text) return
      console.log(text)
      const data = JSON.parse(text)
      const { GPBType, msgBase64 } = data
      if (GPBType === 1666) return
      const method = EncodeOrDecode.getMethod(GPBType)
      let message: DeMessage | undefined = {
        method,
        GPBType,
        payload: data
      }
      if (msgBase64) {
        const decode = new EncodeOrDecode(method)
        message = decode.decode(data)
      }
      if (message) {
        console.log(`RTM %con ${method}-${GPBType} :`, 'color: #FFFFFF;background-color: #4CAF50;', message.payload)
        super.emit(message.method, memberId, message.payload)
      } else {
        console.log(`RTM %con ${method}-${GPBType} 解析为空，可能是分包的子包:`, 'color: #FFFFFF;background-color: #4CAF50;', data)
      }
    })
  }

  public async sendMessage(method: string, payload: object, peerId?: string) {
    if (!this.channelClient) return
    const GPBType = EncodeOrDecode.getGPBType(method)
    const encode = new EncodeOrDecode(method)
    let messageArr: MessageType[] = []
    try {
      messageArr = encode.encode({ ...payload, msgInfo: { msgVersion: this.msgVersion } })
      console.log(`RTM %csend ${method}-${GPBType}`, 'color: #FFFFFF;background-color: black;', payload)
    } catch (error) {
      console.error(`RTM %csend ${method}-${GPBType}`, 'color: #FFFFFF;background-color: black;', payload)
      console.error(error)
    }
    messageArr.forEach((v) => {
      try {
        if (peerId) {
          this.client?.sendMessageToPeer({ text: JSON.stringify(v) }, peerId)
        } else {
          this.channelClient?.sendMessage({ text: JSON.stringify(v) })
        }
      } catch (error) {
        console.error(error)
      }
    })
  }

  public async leave() {
    console.info('rtm leave')
    if (this.channelClient) {
      await this.channelClient.leave().catch((err: any) => {
        console.error('离开频道失败', err)
        return false
      })
      return true
    }
  }

  public async logout() {
    this.removeAllListeners()
    console.info('rtm logout')
    await this.client?.logout().catch((err: any) => {
      console.error('退出登录失败', err)
      return false
    })
    return true
  }
}
